import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, onSnapshot, orderBy } from 'firebase/firestore';
import { ClubEvent, UserProfile, TimeLog } from '../types';
import { cleanIcsString } from '../lib/utils';
import { Clock, LogIn, LogOut, User, Mail, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { safeFormatDate } from '../lib/utils';

export default function TimeTracker() {
  const [email, setEmail] = useState('');
  const [member, setMember] = useState<UserProfile | null>(null);
  const [activeEvents, setActiveEvents] = useState<ClubEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [myLogs, setMyLogs] = useState<TimeLog[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [activeClockIn, setActiveClockIn] = useState<TimeLog | null>(null);

  useEffect(() => {
    // Fetch active muster/volunteer events
    const q = query(
      collection(db, 'events'), 
      where('status', '==', 'active')
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const events = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as ClubEvent))
        .filter(e => ['Muster', 'Volunteer', 'Convention', 'Shift'].includes(e.type));
      setActiveEvents(events);
      setSelectedEventId(prev => {
        if (events.length > 0 && !prev) {
          return events[0].id!;
        }
        return prev;
      });
    });

    return () => unsubscribe();
  }, []);

  const verifyMember = async () => {
    if (!email) return;
    setIsVerifying(true);
    try {
      const q = query(collection(db, 'users'), where('email', '==', email.toLowerCase().trim()));
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        const memberData = { id: snap.docs[0].id, ...snap.docs[0].data() } as UserProfile;
        setMember(memberData);
        fetchLogs(memberData.email);
        toast.success(`Welcome aboard, ${memberData.firstName}!`);
      } else {
        toast.error('Member credentials not found in manifest.');
        setMember(null);
      }
    } catch (err) {
      handleFirestoreError(err, OperationType.GET, 'users');
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchLogs = (memberEmail: string) => {
    const q = query(
      collection(db, 'timelogs'),
      where('email', '==', memberEmail.toLowerCase().trim()),
      orderBy('clockIn', 'desc')
    );

    return onSnapshot(q, (snap) => {
      const logs = snap.docs.map(d => ({ id: d.id, ...d.data() } as TimeLog));
      setMyLogs(logs);
      
      const active = logs.find(l => l.status === 'ACTIVE');
      setActiveClockIn(active || null);
    });
  };

  const handleClockIn = async () => {
    if (!member || !selectedEventId) return;
    
    try {
      await addDoc(collection(db, 'timelogs'), {
        email: member.email.toLowerCase().trim(),
        eventId: selectedEventId,
        clockIn: new Date().toISOString(),
        status: 'ACTIVE'
      });
      toast.success('Duty cycle initiated. Good luck out there!');
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'timelogs');
    }
  };

  const handleClockOut = async () => {
    if (!activeClockIn) return;
    
    try {
      await updateDoc(doc(db, 'timelogs', activeClockIn.id!), {
        clockOut: new Date().toISOString(),
        status: 'COMPLETED'
      });
      toast.success('Duty cycle terminated. Operations logged.');
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'timelogs');
    }
  };

  if (!member) {
    return (
      <div className="max-w-md mx-auto mt-20 p-8 bg-[var(--background)] border-l-8 border-lcars-purple rounded-r-3xl shadow-2xl animate-in fade-in zoom-in">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-lcars-purple text-black rounded-full">
            <Clock className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-lcars-purple tracking-tighter uppercase">Time Tracker</h1>
            <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Personnel Verification Required</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="auth-email" className="text-[0.65rem] text-lcars-tan font-bold uppercase ml-4">Authorized Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-3 w-5 h-5 text-lcars-purple/60" />
              <input 
                id="auth-email"
                type="email"
                placeholder="EMAIL@STARFLEET.COM"
                className="w-full bg-white/5 border-2 border-lcars-purple/30 rounded-full px-12 py-3 text-[var(--foreground)] font-bold tracking-wider focus:border-lcars-purple outline-none transition-all"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verifyMember()}
              />
            </div>
          </div>

          <button 
            onClick={verifyMember}
            disabled={isVerifying || !email}
            className="w-full h-14 lcars-pill bg-lcars-purple text-black font-bold text-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {isVerifying ? 'VERIFYING...' : <><LogIn className="w-6 h-6" /> ACCESS SECURED TERMINAL</>}
          </button>

          <div className="p-4 bg-lcars-purple/5 rounded-2xl border border-lcars-purple/20">
            <p className="text-[0.65rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest leading-relaxed text-center">
              Strategic access to shipboard logs requires level 4 security clearance. Logins are tracked per protocol.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const selectedEvent = activeEvents.find(e => e.id === selectedEventId);

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-[var(--background)] p-6 border-l-8 border-lcars-blue rounded-r-[3rem]">
        <div className="flex items-center gap-6">
          <div className="p-4 bg-lcars-blue text-black rounded-full shadow-lg">
            <User className="w-10 h-10" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-lcars-blue uppercase tracking-tighter">
              {member.firstName} {member.lastName}
            </h2>
            <div className="flex gap-4 mt-1">
              <span className="text-[0.65rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Assignment: {member.currentRank}</span>
              <span className="text-[0.65rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase truncate max-w-[200px]">{member.email}</span>
            </div>
          </div>
        </div>
        <button 
          onClick={() => { setMember(null); setEmail(''); }}
          className="lcars-pill bg-lcars-red text-[var(--foreground)] py-2 px-8 font-bold text-xs uppercase"
        >
          Logout Ops
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Duty Status */}
        <div className="space-y-6">
          <div className="bg-[var(--background)] border-l-8 border-lcars-tan p-8 rounded-r-[2rem] shadow-xl space-y-6">
            <h3 className="text-xl font-bold text-lcars-tan uppercase flex items-center gap-2">
              <Clock className="w-5 h-5" /> Operational Status
            </h3>

            {activeClockIn ? (
              <div className="space-y-6">
                <div className="p-6 bg-lcars-green/10 border-2 border-lcars-green rounded-2xl animate-pulse">
                  <div className="flex items-center gap-4 text-lcars-green">
                    <CheckCircle2 className="w-8 h-8" />
                    <div>
                      <p className="font-black text-2xl uppercase tracking-tighter">Duty Cycle Active</p>
                      <p className="text-xs font-bold opacity-60">Initiated at: {safeFormatDate(activeClockIn.clockIn, 'HH:mm:ss', 'Unknown')}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <p className="text-[0.7rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest">Active Mission</p>
                    <p className="font-bold text-lcars-tan">{activeEvents.find(e => e.id === activeClockIn.eventId)?.name || 'Unknown Operation'}</p>
                  </div>
                  <button 
                    onClick={handleClockOut}
                    className="w-full h-16 lcars-pill bg-lcars-red text-[var(--foreground)] font-bold text-2xl flex items-center justify-center gap-3 transition-all hover:bg-lcars-red/80 active:scale-95 shadow-lg"
                  >
                    <LogOut className="w-8 h-8" /> CLOCK OUT
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="ops-port" className="text-[0.65rem] text-lcars-tan font-bold uppercase ml-4">Select Operations Port</label>
                  <select 
                    id="ops-port"
                    className="w-full bg-white/5 border-2 border-lcars-tan/30 rounded-full px-6 py-3 text-[var(--foreground)] font-bold outline-none focus:border-lcars-tan"
                    value={selectedEventId}
                    onChange={e => setSelectedEventId(e.target.value)}
                  >
                    {activeEvents.map(e => (
                      <option key={e.id} value={e.id}>{cleanIcsString(e.name)}</option>
                    ))}
                    {activeEvents.length === 0 && <option disabled>NO ACTIVE OPERATIONS FOUND</option>}
                  </select>
                </div>

                {selectedEvent && (
                  <div className="p-4 bg-lcars-blue/10 rounded-2xl border border-lcars-blue/30 space-y-2">
                    <div className="flex items-center gap-2 text-lcars-blue text-[0.7rem] font-bold uppercase">
                      <Calendar className="w-4 h-4" /> Mission Logistics
                    </div>
                    <p className="text-sm font-bold">{selectedEvent.location}</p>
                    <p className="text-[0.65rem] text-[var(--foreground)]/60 font-bold uppercase tracking-widest">{selectedEvent.type}</p>
                  </div>
                )}

                <button 
                  onClick={handleClockIn}
                  disabled={activeEvents.length === 0}
                  className="w-full h-16 lcars-pill bg-lcars-blue text-black font-bold text-2xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 shadow-lg disabled:opacity-50"
                >
                  <LogIn className="w-8 h-8" /> CLOCK IN
                </button>
              </div>
            )}
          </div>
        </div>

        {/* History */}
        <div className="bg-[var(--background)] border-l-8 border-lcars-pink p-8 rounded-r-[2rem] shadow-xl overflow-hidden flex flex-col h-[500px]">
          <h3 className="text-xl font-bold text-lcars-pink uppercase flex items-center gap-2 mb-6">
            <Clock className="w-5 h-5" /> Operational History
          </h3>
          
          <div className="flex-1 overflow-auto space-y-3 pr-2 scrollbar-hide">
            {myLogs.map(log => {
              const event = activeEvents.find(e => e.id === log.eventId) || { name: 'Legacy Ops' };
              const duration = log.clockOut 
                ? `${Math.round((new Date(log.clockOut).getTime() - new Date(log.clockIn).getTime()) / (1000 * 60))} Mins`
                : '--';

              return (
                <div key={log.id} className="p-4 bg-white/5 rounded-2xl border-l-4 border-lcars-pink flex justify-between items-center group hover:bg-white/10 transition-all">
                  <div>
                    <p className="font-bold text-sm tracking-tight group-hover:text-lcars-pink transition-colors">{event.name}</p>
                    <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest">
                      {safeFormatDate(log.clockIn, 'MMM dd', 'TBD')} | {safeFormatDate(log.clockIn, 'HH:mm', 'TBD')} - {log.clockOut ? safeFormatDate(log.clockOut, 'HH:mm', 'ACTIVE') : 'ACTIVE'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-lcars-pink">{duration}</p>
                    <p className="text-[0.5rem] text-[var(--foreground)]/40 font-bold tracking-[0.2em] uppercase">Duration</p>
                  </div>
                </div>
              );
            })}
            {myLogs.length === 0 && (
              <div className="text-center py-20 opacity-20">
                <AlertTriangle className="w-12 h-12 mx-auto mb-2" />
                <p className="text-xs font-bold uppercase tracking-widest">No historical logs found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
